import {
  Typenames,
  VlocApplicationDenied,
  VlocNotPreQualifiedCreditFrozen,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack } from 'expo-router';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import theme from 'shared/styles/theme';

type HardPullDeclineAndCreditFrozenScreenProps = {
  data?:
    | VlocApplicationDenied
    | VlocNotPreQualifiedCreditFrozen
    | {
        __typename?:
          | Typenames.VlocApplicationDenied
          | Typenames.VlocNotPreQualifiedCreditFrozen;
        title?: string;
        description?: string;
      };
};

const HardPullDeclineAndCreditFrozenScreen = ({
  data,
}: HardPullDeclineAndCreditFrozenScreenProps) => {
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'creditFrozen',
  });

  const { bottom } = useSafeAreaInsets();

  const { title = '', description = '' } = data ?? {};

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    logPressEventAnalytics();
    dismissToTab(Tabs.HOME);
  };

  useBackHandler(goHome);

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.VLOC_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
    HomeTileTypeIds.VXL_TRACKER_CARD_TILE_CREDIT_TRACKER,
  ]);

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  useViewDidFocusScreenAnalyticsEvent({
    key:
      data?.__typename === Typenames.VlocNotPreQualifiedCreditFrozen
        ? '/varo-line-of-credit/creditFrozen'
        : '/varo-line-of-credit/hardPullDecline',
  });

  const vlocApplicationDeclinedEvent = usePressableAnalytics({
    analyticsId:
      data?.__typename === Typenames.VlocApplicationDenied
        ? 'vlocApplicationDeclined'
        : '',
  });

  const creditFrozenAndGoToHomeClickedEvent = usePressableAnalytics({
    analyticsId: 'creditFrozenAndGoToHomeClicked',
    customAttributes: {
      cta: 'Done',
    },
  });

  const hardPullDeclineAndGoToHomeClickedEvent = usePressableAnalytics({
    analyticsId: 'hardPullDeclineAndGoToHomeClicked',
    customAttributes: {
      cta: 'Done',
    },
  });

  const logPressEventAnalytics = () => {
    if (data?.__typename === Typenames.VlocNotPreQualifiedCreditFrozen) {
      creditFrozenAndGoToHomeClickedEvent();
    }
    if (data?.__typename === Typenames.VlocApplicationDenied) {
      vlocApplicationDeclinedEvent();
      hardPullDeclineAndGoToHomeClickedEvent();
    }
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerTransparent: false,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
          gestureEnabled: false,
          headerShown: true,
          headerBackVisible: false,
        }}
      />
      <View style={styles.contentWrapper}>
        <ScrollView contentContainerStyle={styles.scrollView}>
          {title ? <TitleLgText numberOfLines={0}>{title}</TitleLgText> : null}
          {description ? (
            <BodyBaseText style={styles.body} numberOfLines={0}>
              {description}
            </BodyBaseText>
          ) : null}
        </ScrollView>
        <View style={styles.buttonContainer}>
          <Button label={translatedText('button')} onPress={goHome} />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  body: {
    marginTop: theme.spacing.m,
  },
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentWrapper: {
    flex: 1,
    justifyContent: 'space-between',
  },
  scrollView: {
    flexGrow: 1,
    justifyContent: 'flex-start',
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
});

export default HardPullDeclineAndCreditFrozenScreen;
